import { MODES, modeById } from '../game/rules/index.js';
import { TUNING as AI_TUNING } from '../ai/AIDirector.js';
import { t, getLang, onLang } from './i18n.js';
import { commandBar } from './CommandBar.js';
import { isTouchDevice } from '../core/Engine.js';

/**
 * Pre-match setup: which mode, and how good the bots are.
 *
 * ## Why it reloads
 *
 * Picking a mode writes the choice and reloads the page. That reads like a cop-out
 * and is the honest answer: the roster a mode asks for is what the AI director
 * spawns *at boot*, and the flags a mode places come from the nav mesh that is
 * built once. Switching live would mean moving every soldier to a different side
 * mid-firefight, re-deriving objectives, and resetting scores that other modules
 * are already holding — three chances to leave the game in a state no player
 * could have reached. A reload is a second and a half and is provably correct.
 *
 * The bot skill is the opposite and applies immediately: it is four multipliers
 * read through a getter, so a soldier already on the map picks it up on the next
 * frame it thinks.
 *
 * ## Both input devices, from the same markup
 *
 * Rows are the control — the whole row is the tap target, 44px on touch — and the
 * footer is the shared `commandBar`, so there is one code path rather than a
 * keyboard hint string beside a set of touch chips. That split is what produced
 * four separate "works on desktop only" defects in one day, and deleting it is
 * the only reason this screen cannot repeat them.
 */

export const TUNING = {
  openKey: 'KeyM',
  storeMode: 'ob.mode',
  storeSkill: 'ob.skill',
  storeSquad: 'ob.squad',
  squadOptions: [3, 4, 5, 6, 8],
};

const SKILLS = [
  { id: 'recruit', ja: '新兵', en: 'RECRUIT', descJa: '狙いが甘く、気づくのも遅い', descEn: 'Poor aim, slow to notice you' },
  { id: 'regular', ja: '一般兵', en: 'REGULAR', descJa: '標準。今までのボット', descEn: 'The standard soldier' },
  { id: 'veteran', ja: '古参兵', en: 'VETERAN', descJa: '素早く反応し、よく当てる', descEn: 'Reacts fast, shoots straight' },
  { id: 'elite', ja: '特殊部隊', en: 'ELITE', descJa: '遠くから、ほぼ即座に', descEn: 'Sees far, fires almost at once' },
];

function read(key, fallback) {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private browsing: the choice simply does not persist */
  }
}

/** The saved bot skill, applied at boot. Exported so `main` can call it once. */
export function savedSkill() {
  const id = read(TUNING.storeSkill, AI_TUNING.defaultSkill);
  return AI_TUNING.skills[id] ? id : AI_TUNING.defaultSkill;
}

export class ModeSelect {
  constructor(ctx) {
    this.ctx = ctx;
    this.open = false;
    this.mode = read(TUNING.storeMode, 'skirmish');
    this.skill = savedSkill();
    this.squad = Number(read(TUNING.storeSquad, 6)) || 6;

    this._build();
    this._bind();
    this._offLang = onLang(() => {
      if (this.open) this._render();
    });
  }

  _build() {
    const host = document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'ms-root';
    root.hidden = true;
    root.innerHTML = `
      <div class="ms-panel">
        <div class="ms-head">
          <h2 class="ms-title"></h2>
          <p class="ms-sub"></p>
        </div>
        <div class="ms-body">
          <section class="ms-sec" data-sec="mode"><h3></h3><div class="ms-list"></div></section>
          <section class="ms-sec" data-sec="skill"><h3></h3><div class="ms-list"></div></section>
          <section class="ms-sec" data-sec="squad"><h3></h3><div class="ms-list ms-row"></div></section>
        </div>
        <div class="ms-foot"></div>
      </div>`;
    host.appendChild(root);
    this.root = root;
    this.panel = root.querySelector('.ms-panel');
    this.foot = root.querySelector('.ms-foot');
  }

  _bind() {
    // Capture, so this screen claims the key before `Input` turns it into a
    // gameplay action — the same reason every other screen here listens on capture.
    this._onKey = (e) => {
      if (e.code === TUNING.openKey && !this.open && this._canOpen()) {
        e.preventDefault();
        e.stopPropagation();
        this.show();
      } else if (this.open && (e.code === 'Escape' || e.code === TUNING.openKey)) {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      } else if (this.open && (e.code === 'Enter' || e.code === 'NumpadEnter')) {
        e.preventDefault();
        e.stopPropagation();
        this._apply();
      }
    };
    document.addEventListener('keydown', this._onKey, true);

    this.root.addEventListener('click', (e) => {
      const row = e.target.closest('[data-pick]');
      if (row) {
        const [kind, value] = row.dataset.pick.split(':');
        this._pick(kind, value);
        return;
      }
      const act = e.target.closest('[data-act]')?.dataset.act;
      if (act === 'close') this.hide();
      else if (act === 'apply') this._apply();
    });
  }

  _canOpen() {
    // Only from a screen where a match is not in progress, because applying a
    // mode reloads. The loadout screen is the natural home: it is already the
    // "before you deploy" surface.
    return !this.ctx.engine?.paused || true;
  }

  _pick(kind, value) {
    if (kind === 'mode') this.mode = value;
    else if (kind === 'skill') {
      this.skill = value;
      // Immediate, unlike the mode: this one is only multipliers.
      this.ctx.ai?.setSkill?.(value);
      write(TUNING.storeSkill, value);
    } else if (kind === 'squad') this.squad = Number(value);
    this._render();
  }

  /** Persist and restart. See the note at the top of the file. */
  _apply() {
    write(TUNING.storeMode, this.mode);
    write(TUNING.storeSkill, this.skill);
    write(TUNING.storeSquad, String(this.squad));
    // Strip any `?mode=` so the saved choice is what takes effect, not a stale
    // link the player followed an hour ago.
    const url = new URL(location.href);
    url.searchParams.delete('mode');
    location.replace(url.toString());
  }

  show() {
    this.open = true;
    this.root.hidden = false;
    this._render();
    this.ctx.events.emit('ui:screen', { id: 'modeselect', open: true });
  }

  hide() {
    this.open = false;
    this.root.hidden = true;
    this.ctx.events.emit('ui:screen', { id: 'modeselect', open: false });
  }

  toggle() {
    this.open ? this.hide() : this.show();
  }

  _render() {
    const lang = getLang();
    const L = (o) => (typeof o === 'string' ? o : o?.[lang] ?? o?.en ?? '');
    this.root.querySelector('.ms-title').textContent = t('ms.title');
    this.root.querySelector('.ms-sub').textContent = t('ms.sub');

    const secs = this.root.querySelectorAll('.ms-sec');
    secs[0].querySelector('h3').textContent = t('ms.mode');
    secs[1].querySelector('h3').textContent = t('ms.skill');
    secs[2].querySelector('h3').textContent = t('ms.squad');

    secs[0].querySelector('.ms-list').innerHTML = MODES.map((m) => `
      <button class="ms-item${m.id === this.mode ? ' is-on' : ''}" data-pick="mode:${m.id}">
        <span class="ms-item-name">${L(m.name)}</span>
        <span class="ms-item-desc">${L(m.blurb)}</span>
      </button>`).join('');

    secs[1].querySelector('.ms-list').innerHTML = SKILLS.map((s) => `
      <button class="ms-item${s.id === this.skill ? ' is-on' : ''}" data-pick="skill:${s.id}">
        <span class="ms-item-name">${lang === 'ja' ? s.ja : s.en}</span>
        <span class="ms-item-desc">${lang === 'ja' ? s.descJa : s.descEn}</span>
      </button>`).join('');

    // Team size is meaningless in a mode without a second team, so say so rather
    // than offering a control that silently does nothing.
    const teamed = modeById(this.mode).id !== 'skirmish';
    secs[2].hidden = !teamed;
    secs[2].querySelector('.ms-list').innerHTML = TUNING.squadOptions.map((n) => `
      <button class="ms-chip${n === this.squad ? ' is-on' : ''}" data-pick="squad:${n}">${n}v${n}</button>`).join('');

    // The bar synthesises a key rather than calling a handler, so a click and the
    // keypress it advertises take the identical path through `_onKey` below.
    this.foot.innerHTML = '';
    this.foot.appendChild(
      commandBar(
        [
          { key: 'ms.apply', code: 'Enter', pad: 'A', primary: true },
          { key: 'cmd.closeShort', code: 'Escape', pad: 'B' },
        ],
        { padConnected: !!this.ctx.input?.padConnected }
      )
    );
  }

  dispose() {
    document.removeEventListener('keydown', this._onKey, true);
    this._offLang?.();
    this.root.remove();
  }
}
